import { proposalKind } from '@/domain/health';
import type { ComponentConfig, Proposal } from '@/domain/types';

export type QueueSection = 'failures' | 'recoveries' | 'failed_publishes';

/** The three groups the approval queue separates (TDD §5.3 "Approval queue"). */
export function sectionOf(proposal: Proposal): QueueSection {
  if (proposal.status === 'publish_failed') return 'failed_publishes';
  if (proposal.to_status === 'ok') return 'recoveries';
  return 'failures';
}

export const SECTION_TITLE: Record<QueueSection, string> = {
  failures: 'Failures and de-escalations',
  recoveries: 'Recoveries awaiting confirmation',
  failed_publishes: 'Publishes that failed',
};

/** Short wording for what kind of change a proposal asks for. */
export function kindLabel(proposal: Proposal): string {
  if (proposal.status === 'publish_failed') return 'Publish failed';
  switch (proposalKind(proposal.from_status, proposal.to_status)) {
    case 'recovery':
      return 'Recovery to confirm';
    case 'escalation':
      return 'Failure';
    case 'de-escalation':
      return 'De-escalation';
  }
}

/** How long the situation behind the proposal has lasted: from its oldest evidence run. */
export function problemSince(proposal: Proposal): string {
  const oldest = proposal.evidence[proposal.evidence.length - 1];
  return oldest?.completed_at ?? proposal.created_at;
}

/** A manual recovery has waited longer than the component allows (§8.4 recovery_approval_alert_min). */
export function recoveryOverdue(
  proposal: Proposal,
  config: ComponentConfig | undefined,
  nowMs: number,
): boolean {
  if (proposal.to_status !== 'ok' || proposal.status !== 'open' || !config) return false;
  return nowMs - Date.parse(proposal.created_at) > config.recovery_approval_alert_min * 60_000;
}

/** Humanise reasons such as fail_streak_6 or pass_streak_2. */
export function reasonLabel(reason: string): string {
  const fail = /^fail_streak_(\d+)$/.exec(reason);
  if (fail) return `${fail[1]} failing runs in a row`;
  const pass = /^pass_streak_(\d+)$/.exec(reason);
  if (pass) return `${pass[1]} passing runs in a row`;
  if (reason === 'manual') return 'Manual change';
  return reason.replaceAll('_', ' ');
}
